import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import type { Analytics } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: "AIzaSyCtpq8NuUQnaQyt_idpSPQV1_1CrnA9YyQ",
  authDomain: "auth.moddy.ai",
  projectId: "moddy-web",
  storageBucket: "moddy-web.firebasestorage.app",
  messagingSenderId: "140837057875",
  appId: "1:140837057875:web:4d1b97df984a349fa85da6",
  measurementId: "G-NYVY83ZJXP"
};

class FirebaseService {
  private static instance: FirebaseService;
  private app: FirebaseApp;
  private auth: Auth;
  private analytics: Analytics | null = null;
  private isInitialized = false;
  private analyticsPromise: Promise<void> | null = null;

  private constructor() {
    // Initialize Firebase
    this.app = initializeApp(firebaseConfig);
    this.auth = getAuth(this.app);
    setPersistence(this.auth, browserLocalPersistence);
    this.isInitialized = true;

    // Initialize analytics asynchronously in browser
    if (typeof window !== 'undefined') {
      this.analyticsPromise = this.initializeAnalytics();
    }
  }

  private async initializeAnalytics(): Promise<void> {
    try {
      const { getAnalytics } = await import('firebase/analytics');
      this.analytics = getAnalytics(this.app);
      
      // Enable Firebase debug logging in development
      if (import.meta.env.DEV) {
        console.log('Firebase debug logging enabled');
      }

    } catch (error) {
      if (error instanceof Error && error.message.includes('content blocker')) {
        console.warn('Firebase Analytics blocked by content blocker');
      } else {
        console.warn('Firebase Analytics initialization failed:', error);
      }
      this.analytics = null;
    }
  }

  public static getInstance(): FirebaseService {
    if (!FirebaseService.instance) {
      FirebaseService.instance = new FirebaseService();
    }
    return FirebaseService.instance;
  }

  public getAuth(): Auth {
    if (!this.isInitialized) {
      throw new Error('Firebase not initialized');
    }
    return this.auth;
  }

  public getApp(): FirebaseApp {
    if (!this.isInitialized) {
      throw new Error('Firebase not initialized');
    }
    return this.app;
  }

  public isReady(): boolean {
    return this.isInitialized;
  }

  public async isAnalyticsAvailable(): Promise<boolean> {
    if (this.analyticsPromise) {
      await this.analyticsPromise;
    }
    return this.analytics !== null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Firebase Analytics logEvent accepts arbitrary event params
  public async trackEvent(eventName: string, params?: Record<string, any>): Promise<void> {
    if (this.analyticsPromise) {
      await this.analyticsPromise;
    }
    
    if (!this.analytics) {
      if (import.meta.env.DEV) {
        console.log('Analytics event not sent (analytics blocked):', { eventName, params });
      }
      return;
    }

    try {
      const { logEvent } = await import('firebase/analytics');
      await logEvent(this.analytics, eventName, params);
      if (import.meta.env.DEV) {
        // console.log('Analytics event sent:', { eventName, params });
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('Failed to track event:', error);
      }
      this.analytics = null;
    }
  }


}

// Export a singleton instance
export const firebaseService = FirebaseService.getInstance();

// Export convenience methods
export const getFirebaseAuth = () => firebaseService.getAuth();
export const getFirebaseApp = () => firebaseService.getApp(); 